import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import PermMediaRoundedIcon from '@mui/icons-material/PermMediaRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import UploadRoundedIcon from '@mui/icons-material/UploadRounded';
import { Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { usePermissions } from '@/shared/permissions';
import { useTenant } from '@/shared/tenant/useTenant';
import { EmptyState, SectionScaffold } from '@/shared/ui';

/**
 * Media section — the media library for the active tenant.
 *
 * Promoted to a standalone section (out of the settings hub): media is an asset
 * workspace, not a one-off configuration screen. The asset grid (search + upload)
 * will render here once the media data API is wired; the gear opens the media
 * rules (formats, variants, CDN).
 */
export function MediaPage() {
  const { t } = useTranslation('app');
  const { hasPermission } = usePermissions();
  const { tenantName } = useTenant();
  const [search, setSearch] = useState('');

  const canView = hasPermission('settings.media.view');
  const tenantLabel = useMemo(() => tenantName ?? t('media.fallbackTenant'), [tenantName, t]);

  return (
    <SectionScaffold
      title={t('media.title')}
      subtitle={t('media.subtitle')}
      canView={canView}
      settingsTo="/settings/media"
      settingsLabel={t('media.settingsLabel')}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: t('media.searchPlaceholder'),
        disabled: true,
        disabledHint: t('media.searchDisabledHint'),
      }}
      primaryAction={
        <Button variant="contained" startIcon={<UploadRoundedIcon />} disabled>
          {t('media.uploadButton')}
        </Button>
      }
    >
      <EmptyState
        icon={<PermMediaRoundedIcon sx={{ fontSize: 48 }} />}
        title={t('media.empty.title')}
        description={t('media.empty.description', { tenant: tenantLabel })}
        action={
          <Button
            variant="outlined"
            component={RouterLink}
            to="/settings/media"
            startIcon={<SettingsRoundedIcon />}
          >
            {t('media.empty.action')}
          </Button>
        }
      />
    </SectionScaffold>
  );
}
